import { Component, OnInit, computed, ElementRef, HostListener, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { SessionContextService } from '../../core/services/session-context.service';
import { SelectionService } from '../../core/services/selection.service';
import { CaseContextService } from '../../core/services/case-context.service';
import { CaseApiService } from '../../core/services/case-api.service';
import { TopbarPopoverService } from './topbar-popover.service';
import { BasketDropdownComponent } from './dropdowns/basket-dropdown.component';
import { CasesDropdownComponent } from './dropdowns/cases-dropdown.component';
import { PlaceholderDropdownComponent } from './dropdowns/placeholder-dropdown.component';
import { DevUserService } from '../../features/admin/dev-user.service';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-topbar-v3',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './topbar-v3.component.html',
  styleUrl: './topbar-v3.component.scss',
})
export class TopbarV3Component implements OnInit {
  private router = inject(Router);
  private host = inject(ElementRef);
  private readonly currentUrl = toSignal(
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
    { initialValue: null },
  );

  protected session = inject(SessionContextService);
  protected selection = inject(SelectionService);
  private caseCtx = inject(CaseContextService);
  private caseApi = inject(CaseApiService);
  private popover = inject(TopbarPopoverService);
  private devUser = inject(DevUserService);
  private auth = inject(AuthService);

  protected readonly menuOpen = signal(false);

  protected readonly devUserLoaded = computed(() => !!this.devUser.currentUser());

  protected basketCount = computed(
    () => this.selection.productCount() + this.selection.benefitCount(),
  );

  protected casesCount = signal(0);
  protected inboxCount = signal(0);
  protected bellCount = signal(0);

  protected caseLoaded = computed(() => !!this.caseCtx.activeCase());
  protected caseName = computed(() => this.caseCtx.activeCase()?.name ?? 'No case');
  protected caseSummary = computed(() => {
    const c = this.caseCtx.activeCase();
    if (!c) return '';
    const parts: string[] = [];
    if (c.age != null) parts.push(String(c.age));
    if (c.state) parts.push(c.state);
    const prem = this.fmtPremium(c.premium);
    if (prem) parts.push(prem);
    return parts.join(' · ');
  });
  protected caseChipTitle = computed(() => {
    const c = this.caseCtx.activeCase();
    return c
      ? `Case ${c.caseCode} · ${c.stage} — click to switch or clear`
      : 'No case active — click to load a case';
  });

  private fmtPremium(p: number | null): string {
    if (p == null) return '';
    if (p >= 1_000_000) {
      const m = p / 1_000_000;
      return '$' + (Number.isInteger(m) ? m.toFixed(0) : m.toFixed(1)) + 'M';
    }
    if (p >= 1_000) return '$' + Math.round(p / 1_000) + 'k';
    return '$' + p;
  }

  protected displayName = computed(() => this.session.displayName());
  protected userInitials = computed(() => {
    const name = this.session.displayName();
    if (!name) return 'JW';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length === 0) return 'JW';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  });

  onCmdKClick(): void {
    console.info('[TopbarV3] ⌘K — coming soon');
  }

  onCaseChipClick(event: Event): void {
    this.openCasesDropdown(event.currentTarget as HTMLElement);
  }

  onSubscriptionsClick(event: Event): void {
    this.popover.open({
      component: PlaceholderDropdownComponent,
      anchor: event.currentTarget as HTMLElement,
      data: { title: 'Subscriptions', subtext: 'Subscription notifications and digest preferences.' },
    });
  }

  onInboxIconClick(event: Event): void {
    this.popover.open({
      component: PlaceholderDropdownComponent,
      anchor: event.currentTarget as HTMLElement,
      data: { title: 'Inbox', subtext: 'Messages from your team and clients.' },
    });
  }

  private openCasesDropdown(anchor: HTMLElement): void {
    this.popover.open({ component: CasesDropdownComponent, anchor });
  }

  ngOnInit(): void {
    this.caseApi.list().subscribe({
      next: (rows) => this.casesCount.set(rows.length),
      error: () => this.casesCount.set(0),
    });
  }

  onBasketIconClick(event: Event): void {
    this.popover.open({ component: BasketDropdownComponent, anchor: event.currentTarget as HTMLElement });
  }

  onBellClick(event: Event): void {
    this.popover.open({
      component: PlaceholderDropdownComponent,
      anchor: event.currentTarget as HTMLElement,
      data: { title: 'Notifications', subtext: 'System alerts and digest items.' },
    });
  }

  toggleMenu(): void { this.menuOpen.update((v) => !v); }
  closeMenu(): void { this.menuOpen.set(false); }

  onToggleNewShell(): void {
    this.closeMenu();
    this.session.setEnableHorizontalNav(!this.session.enableHorizontalNav());
  }

  navigate(path: string): void {
    this.closeMenu();
    this.router.navigateByUrl(path);
  }

  signOut(): void {
    this.closeMenu();
    // Explicit sign-out is the one path that should also discard the selected
    // user — automatic stale-credential cleanup leaves it in place.
    this.auth.logout({ clearDevUser: true });
  }

  exitDevUser(): void {
    this.closeMenu();
    this.devUser.clearUser();
  }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent): void {
    const target = ev.target as Node;
    if (!this.host.nativeElement.contains(target) && this.menuOpen()) {
      this.closeMenu();
    }
  }
}
